// Meta Pixel (browser) — Meta Ads launch 15/07. PostHog stays the internal
// source of truth; every Meta event fires SIDE BY SIDE with an existing
// PostHog capture (same trigger), never replacing it.
//
// Design rules:
//   - Init is gated on VITE_META_PIXEL_ID. Env absent → every function here
//     is a silent no-op (dev/preview builds never hit Meta).
//   - Every track() generates an eventID (uuid) and RETURNS it so callers can
//     hand the same id to the CAPI edge function (meta-capi) — Meta dedupes
//     browser+server pairs on (event_name, event_id).
//   - fbclid is persisted to sessionStorage on first load: OAuth redirects
//     can lose the _fbc cookie mid-flow, and the CAPI needs fbc for matching.
//   - LGPD: the pixel respects a consent flag (below). There is NO cookie
//     banner yet (known gap) — consent defaults to granted. When the banner
//     ships, call setMetaConsent(false) BEFORE initMetaPixel() for opted-out
//     users and setMetaConsent(true) on accept; fbq's own consent queue
//     handles the ordering ('consent' revoke/grant is honored by the snippet
//     queue even before the script loads).

/* eslint-disable @typescript-eslint/no-explicit-any */

const PIXEL_ID: string | undefined = import.meta.env.VITE_META_PIXEL_ID;
const FBCLID_KEY = "vyv_fbclid";

type Fbq = (...args: any[]) => void;

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

let initialized = false;
let consentGranted = true; // LGPD integration point — see header note.
let lastPageViewPath: string | null = null;

function enabled(): boolean {
  return typeof window !== "undefined" && !!PIXEL_ID;
}

function fbq(...args: any[]): void {
  if (!enabled() || !window.fbq) return;
  window.fbq(...args);
}

// Consent gate for the future cookie banner. Callable before OR after init:
// fbq's stub queues 'consent' calls until the script loads.
export function setMetaConsent(granted: boolean): void {
  consentGranted = granted;
  if (!enabled()) return;
  ensureStub();
  fbq("consent", granted ? "grant" : "revoke");
}

// Official fbq bootstrap (Meta's snippet, un-minified) — loads the script tag
// and installs the command queue stub.
function ensureStub(): void {
  if (!enabled() || window.fbq) return;
  const fbqStub: any = function (...args: any[]) {
    if (fbqStub.callMethod) {
      fbqStub.callMethod(...args);
    } else {
      fbqStub.queue.push(args);
    }
  };
  window.fbq = fbqStub;
  if (!window._fbq) window._fbq = fbqStub;
  fbqStub.push = fbqStub;
  fbqStub.loaded = true;
  fbqStub.version = "2.0";
  fbqStub.queue = [];
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  script.onerror = () => console.error("[metaPixel] fbevents.js failed to load");
  document.head.appendChild(script);
}

// Persist fbclid from the landing URL — survives the OAuth redirect round
// trip that can drop the _fbc cookie. getMetaFbc() below reconstructs the
// _fbc format from it when the cookie is missing.
function persistFbclid(): void {
  try {
    const fbclid = new URLSearchParams(window.location.search).get("fbclid");
    if (fbclid) {
      sessionStorage.setItem(FBCLID_KEY, JSON.stringify({ fbclid, ts: Date.now() }));
    }
  } catch (e) {
    console.error("[metaPixel] fbclid persist failed:", e);
  }
}

function readCookie(name: string): string | undefined {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : undefined;
}

// _fbp cookie (browser id) for the CAPI payload.
export function getMetaFbp(): string | undefined {
  if (typeof document === "undefined") return undefined;
  return readCookie("_fbp");
}

// _fbc cookie (click id), reconstructed from the persisted fbclid when the
// cookie is gone (format per Meta docs: fb.1.<creation ms>.<fbclid>).
export function getMetaFbc(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const cookie = readCookie("_fbc");
  if (cookie) return cookie;
  try {
    const raw = sessionStorage.getItem(FBCLID_KEY);
    if (!raw) return undefined;
    const { fbclid, ts } = JSON.parse(raw) as { fbclid: string; ts: number };
    return "fb.1." + ts + "." + fbclid;
  } catch {
    return undefined;
  }
}

// SHA-256 (lowercase hex) for Advanced Matching / CAPI user_data. Normalizes
// per Meta spec: trim + lowercase before hashing.
export async function sha256Normalized(value: string): Promise<string> {
  const data = new TextEncoder().encode(value.trim().toLowerCase());
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export function initMetaPixel(): void {
  if (!enabled() || initialized) return;
  initialized = true;
  persistFbclid();
  ensureStub();
  fbq("consent", consentGranted ? "grant" : "revoke");
  fbq("init", PIXEL_ID);
  fbq("track", "PageView");
  // Remember the landing pathname so AnalyticsBridge's mount-time call does
  // not double-fire PageView for the first route.
  lastPageViewPath = window.location.pathname;
}

// Advanced Matching: once the user is authenticated, re-init with the hashed
// email — Meta merges it into the same pixel instance. Callers pass the RAW
// email; hashing happens here (fbq also accepts raw `em` and hashes
// client-side, but hashing ourselves keeps the raw address out of the wire).
export async function metaIdentify(email: string | undefined | null): Promise<void> {
  if (!enabled() || !email) return;
  try {
    const em = await sha256Normalized(email);
    fbq("init", PIXEL_ID, { em });
  } catch (e) {
    console.error("[metaPixel] identify failed:", e);
  }
}

// Advanced Matching phone — E.164 digits (country code + number, no "+"),
// hashed before it touches the wire. Fired where the product already collects
// a phone (CheckoutPremium form) — never a new collection point.
export async function metaIdentifyPhone(e164Digits: string | undefined | null): Promise<void> {
  if (!enabled() || !e164Digits) return;
  try {
    const ph = await sha256Normalized(e164Digits);
    fbq("init", PIXEL_ID, { ph });
  } catch (e) {
    console.error("[metaPixel] phone identify failed:", e);
  }
}

// ── Correspondência avançada MANUAL (31/07) ─────────────────────────────────
//
// POR QUE: a entrega de evento está em ~100% (1.341 cadastros no banco vs
// ~1,3 mil CompleteRegistration no Gerenciador, 03–31/07), mas só 2 de 10
// cadastros carregam fbc/fbclid — sem o clique, o Meta atribui casando DADOS
// DO USUÁRIO. A correspondência automática ligada no Gerenciador só lê campo
// de FORMULÁRIO, e a maioria dos cadastros é OAuth Google/Apple, onde ninguém
// digita nada. Este caminho manual entrega os dados direto no terceiro
// parâmetro do fbq('init').
//
// CONTRATO COM O SERVIDOR (o invariante que os testes pinam): o pixel hasheia
// SOZINHO o que recebe aqui — os valores saem CRUS deste módulo (diferente de
// metaIdentify/metaIdentifyPhone acima, que pré-hasheiam; o pixel aceita as
// duas formas e produz o MESMO hash, então os caminhos convivem). Para o
// match browser×servidor fechar, o valor cru DEPOIS da normalização do pixel
// tem que ser byte-a-byte o input que o servidor hasheia em
// _shared/metaCapi.ts sha256Normalized (trim + lowercase):
//   em          → trim+lowercase AQUI = o mesmo transform do servidor;
//   external_id → o MESMO user_id que meta-capi-signup-dispatch envia
//                 (uuid do Supabase; trim+lowercase é identidade nele);
//   fn          → só o cliente envia (o servidor não manda nome) — segue a
//                 regra do Meta: lowercase, sem pontuação;
//   ph          → whatsapp_links.wa_phone (E.164 sem '+', owner-RLS), com a
//                 MESMA regra do asaas-webhook: só dígitos com país. No
//                 Brasil é o matcher mais forte depois do e-mail. Sem link
//                 de WhatsApp o campo é omitido; o checkout segue cobrindo
//                 na compra via metaIdentifyPhone.
// db/ge (nascimento/gênero) NÃO entram — o VYV não coleta em formulário e só
// aumentam superfície de privacidade.

/** trim+lowercase — ESPELHO do pré-hash de sha256Normalized no servidor. */
export function normalizeEmailForMatch(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  const v = raw.trim().toLowerCase();
  return v.includes("@") ? v : undefined;
}

/** Idem — em uuid canônico do Supabase o transform é identidade. */
export function normalizeExternalIdForMatch(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  const v = raw.trim().toLowerCase();
  return v.length > 0 ? v : undefined;
}

/**
 * Regra do Meta para nome: lowercase, sem pontuação (UTF-8 ok — acentos
 * ficam). Implementação determinística: mantém só letras e espaços,
 * colapsa espaços, apara as pontas.
 */
export function normalizeNameForMatch(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  const v = raw
    .toLowerCase()
    .replace(/[^\p{L}\s]/gu, "")
    .replace(/\s+/g, " ")
    .trim();
  return v.length > 0 ? v : undefined;
}

/**
 * MESMA regra do asaas-webhook (index.ts:411, Purchase):
 * `waPhone.replace(/\D/g, "")` — só dígitos, COM código do país, sem '+'.
 * A fonte é whatsapp_links.wa_phone, que já é E.164 sem '+' (100% das 233
 * linhas em 31/07), então o transform costuma ser identidade — mas fica
 * pinado por teste contra sha256Normalized para nunca divergir do servidor.
 */
export function normalizePhoneForMatch(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  const digits = raw.replace(/\D/g, "");
  return digits.length > 0 ? digits : undefined;
}

/**
 * Monta o terceiro parâmetro do fbq('init') — VALORES CRUS normalizados,
 * nunca hasheados (o pixel hasheia; ver contrato no cabeçalho). Pura e
 * exportada para o teste pinar a paridade de hash com o servidor.
 */
export function buildAdvancedMatch(input: {
  email?: string | null;
  userId?: string | null;
  firstName?: string | null;
  phone?: string | null;
}): Record<string, string> {
  const ud: Record<string, string> = {};
  const em = normalizeEmailForMatch(input.email);
  const externalId = normalizeExternalIdForMatch(input.userId);
  const fn = normalizeNameForMatch(input.firstName);
  const ph = normalizePhoneForMatch(input.phone);
  if (em) ud.em = em;
  if (externalId) ud.external_id = externalId;
  if (fn) ud.fn = fn;
  if (ph) ud.ph = ph;
  return ud;
}

/**
 * Último passo antes do fbq: hasheia SÓ o external_id.
 *
 * Por quê só ele: em/fn/ph são PII clássica que o fbevents comprovadamente
 * hasheia sozinho (o precedente metaIdentify pré-hasheado casa em produção —
 * o pixel aceita as duas formas). Para external_id a documentação não crava
 * o comportamento na wire, e o proxy deste ambiente bloqueia a sonda
 * empírica (403 em connect.facebook.net, 31/07). Pré-hashear é correto nos
 * DOIS mundos: se o pixel hashearia, produz o MESMO hex (64-hex é detectado
 * como já-hasheado e repassado); se mandaria cru, isto garante que a wire
 * carrega EXATAMENTE o sha256Normalized(user_id) que o CAPI de servidor
 * manda (_shared/metaCapiSignup.ts) — match simétrico por construção, e
 * nenhum uuid em claro para a Meta. Pinado por teste.
 */
export async function finalizeAdvancedMatch(
  ud: Record<string, string>,
): Promise<Record<string, string>> {
  if (!ud.external_id) return ud;
  return { ...ud, external_id: await sha256Normalized(ud.external_id) };
}

// Sessão já identificada nesta carga de página — o onAuthStateChange re-entrega
// INITIAL_SESSION / SIGNED_IN / TOKEN_REFRESHED para o MESMO usuário várias
// vezes; o estado do pixel vive por page load, então uma vez por usuário por
// carga basta. Módulo, não React: remount não pode resetar.
let advancedMatchedUserId: string | null = null;

/**
 * Re-inicializa o pixel com correspondência avançada quando a sessão existe —
 * chamado no login E na restauração de sessão (App.tsx, onAuthStateChange).
 * Fire-and-forget: matching de anúncio nunca bloqueia o boot. Falha na busca
 * do first_name degrada para em+external_id (o match principal), com warn —
 * §11 critério (b): o fallback preserva o que importa.
 */
export async function metaAdvancedMatch(
  userId: string | null | undefined,
  email: string | null | undefined,
): Promise<void> {
  if (!enabled() || !userId) return;
  if (advancedMatchedUserId === userId) return;
  advancedMatchedUserId = userId;
  let firstName: string | undefined;
  let phone: string | undefined;
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    // Duas tabelas sem FK entre si → o PostgREST não embute numa requisição
    // só; uma chamada única exigiria uma RPC nova (migration). Promise.all
    // dispara as duas em PARALELO na mesma conexão: o relógio de parede é o
    // da mais lenta (~o mesmo de uma), o custo real é +1 requisição HTTP por
    // usuário por page load.
    // whatsapp_links: MESMO filtro do asaas-webhook (index.ts:404) —
    // active = true; owner-RLS já permite o SELECT da própria linha (padrão
    // do useWhatsAppLink).
    const [profile, wa] = await Promise.all([
      supabase.from("user_profiles").select("first_name").eq("user_id", userId).maybeSingle(),
      supabase
        .from("whatsapp_links")
        .select("wa_phone")
        .eq("user_id", userId)
        .eq("active", true)
        .maybeSingle(),
    ]);
    if (profile.error) {
      console.warn("[metaPixel] advanced match first_name lookup failed:", profile.error);
    } else {
      firstName = profile.data?.first_name ?? undefined;
    }
    if (wa.error) {
      console.warn("[metaPixel] advanced match wa_phone lookup failed:", wa.error);
    } else {
      phone = wa.data?.wa_phone ?? undefined;
    }
  } catch (e) {
    console.warn("[metaPixel] advanced match profile fetch threw:", e);
  }
  const ud = buildAdvancedMatch({ email, userId, firstName, phone });
  if (Object.keys(ud).length === 0) return;
  fbq("init", PIXEL_ID, await finalizeAdvancedMatch(ud));
}

// SPA route change — AnalyticsBridge calls this next to the PostHog pageview.
// Dedupes consecutive same-path calls (incl. the bridge's mount-time run,
// which follows initMetaPixel's own PageView).
export function metaPageView(pathname: string): void {
  if (!enabled() || !initialized) return;
  if (pathname === lastPageViewPath) return;
  lastPageViewPath = pathname;
  fbq("track", "PageView");
}

export type MetaStandardEvent =
  | "ViewContent"
  | "Lead"
  | "CompleteRegistration"
  | "InitiateCheckout"
  | "AddPaymentInfo"
  | "Purchase";

// event_id DETERMINÍSTICO do CompleteRegistration: "signup-" + user_id.
// É a chave da deduplicação em TRÊS caminhos — pixel do navegador, relay
// meta-capi e o evento de SERVIDOR que o meta-capi-signup-dispatch emite a
// partir da linha em auth.users (que dispara mesmo quando o fbevents.js
// nunca carregou, o caso webview). O Meta deduplica por (event_name,
// event_id) em 48h; três cópias com o mesmo id contam UMA.
// Duplicada em supabase/functions/_shared/metaCapiSignup.ts (edge não
// importa src/) e PINADA por src/test/metaCapiSignup.test.ts — mudar um lado
// sem o outro derruba o teste, não a contagem de conversões.
export function signupEventId(userId: string): string {
  return "signup-" + userId;
}

// Fire a standard event with a fresh eventID; returns the id so the caller
// can send the SAME id to meta-capi for browser+server dedupe. Returns
// undefined when the pixel is disabled (env absent).
// `explicitEventId` existe para eventos com par de SERVIDOR independente do
// navegador (CompleteRegistration): o id determinístico é o que permite ao
// servidor emitir o MESMO id sem coordenação com este cliente.
export function metaTrack(
  event: MetaStandardEvent,
  customData?: Record<string, unknown>,
  explicitEventId?: string,
): string | undefined {
  if (!enabled()) return undefined;
  const eventID = explicitEventId || crypto.randomUUID();
  fbq("track", event, customData ?? {}, { eventID });
  return eventID;
}

// Relay the SAME event to the Conversions API via the meta-capi edge
// function (authed sessions only — the fn is verify_jwt). Fire-and-forget:
// ad tracking must never block or break a product flow. The server enriches
// with client IP + user-agent; we pass the browser ids (fbp/fbc, with fbc
// reconstructed from the persisted fbclid) and the hashed email when known.
export async function sendMetaCapi(
  eventName: MetaStandardEvent,
  eventId: string | undefined,
  customData?: Record<string, unknown>,
  email?: string | null,
): Promise<void> {
  if (!enabled() || !eventId) return;
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    // meta-capi is verify_jwt: without a session (e.g. email signup awaiting
    // confirmation) the relay would 401 — skip quietly; the browser pixel
    // event already fired and the server pair resumes for authed flows.
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const em = email ? await sha256Normalized(email) : undefined;
    const { error } = await supabase.functions.invoke("meta-capi", {
      body: {
        event_name: eventName,
        event_id: eventId,
        event_source_url: window.location.href,
        user_data: { em, fbp: getMetaFbp(), fbc: getMetaFbc() },
        custom_data: customData ?? {},
      },
    });
    if (error) console.error("[metaPixel] capi relay failed:", error);
  } catch (e) {
    console.error("[metaPixel] capi relay threw:", e);
  }
}
